import { useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import set from 'lodash/set';
import { ArrayField, FunctionField, RecordContextProvider, TextField, useRecordContext } from 'react-admin';
import ReportLink from '@/pages/Candidate/view/oneOffOrder/ReportLink';
import { SecondaryDatagridStyled } from '@/styles/overrides';
import { formatStoredReportDate } from '@/pages/Candidate/view/oneOffOrder/formatUtc';
import { OneOffChecksTableProps } from '@/pages/Candidate/view/tables/OneOffChecksTable/types';
import type { OneOffCheckReport } from '@/pages/Candidate/view/tables/OneOffChecksTable/types';
import OneOffCheckReportExpand from '@/pages/Candidate/view/tables/OneOffChecksTable/OneOffCheckReportExpand';
import { sortOneOffChecksByCreatedAtDesc } from '@/pages/Candidate/view/tables/OneOffChecksTable/sortOneOffChecksByCreatedAtDesc';
import OrderFactLines, { CheckTitle } from '@/pages/Candidate/view/oneOffOrder/OrderFactLines';
import ViewInVendor from '@/pages/Candidate/view/oneOffOrder/ViewInVendor';
import { hasVendorPortal } from '@/pages/Candidate/view/oneOffOrder/constants';
import { orderVendor } from '@/pages/Candidate/view/oneOffOrder/orderFacts';
import { DatagridSx, LinksCell, ReportNameContainer } from './styled';

const isRowExpandable = (record: OneOffCheckReport) =>
  Array.isArray(record?.custom_fields) && record.custom_fields.length > 0;

const OneOffChecksTable = ({ source }: OneOffChecksTableProps) => {
  const record = useRecordContext();
  const reports = (get(record, source) ?? []) as OneOffCheckReport[];
  const anyRowHasCustomFields = reports.some((r) => Array.isArray(r?.custom_fields) && r.custom_fields.length > 0);

  const sortedRecord = useMemo(() => {
    if (record == null) return null;
    const base = cloneDeep(record) as Record<string, unknown>;
    const raw = ((get(base, source) as OneOffCheckReport[] | undefined) ?? []) as OneOffCheckReport[];
    set(base, source, sortOneOffChecksByCreatedAtDesc(raw));
    return base;
  }, [record, source]);

  const grid = (
    <ArrayField source={source}>
      <SecondaryDatagridStyled
        bulkActionButtons={false}
        rowClick={false}
        sx={DatagridSx}
        {...(anyRowHasCustomFields ? { expand: <OneOffCheckReportExpand />, isRowExpandable } : {})}
      >
        <FunctionField
          label="Name"
          sortable={false}
          render={(row: OneOffCheckReport) => {
            return (
              <ReportNameContainer>
                <CheckTitle>{row?.name || row?.description || '—'}</CheckTitle>
                <OrderFactLines source={row} />
              </ReportNameContainer>
            );
          }}
        />
        <TextField source="state" label="State" sortable={false} />
        <TextField source="resolution" label="Resolution" sortable={false} />
        <TextField source="source" label="Recheck ID" sortable={false} />
        <FunctionField
          label="Created At"
          sortable={false}
          render={(row: OneOffCheckReport) => formatStoredReportDate(row?.created_at) || '—'}
        />
        <FunctionField
          label="Links"
          sortable={false}
          render={(row: OneOffCheckReport) => {
            const report = row?.report_url ? (
              <ReportLink href={row.report_url} originalName={row.original_file_name} />
            ) : null;
            const vendor = hasVendorPortal(orderVendor(row ?? {}));
            if (!report && !vendor) return null;
            return (
              <LinksCell>
                {vendor ? <ViewInVendor source={row} /> : null}
                {report}
              </LinksCell>
            );
          }}
        />
      </SecondaryDatagridStyled>
    </ArrayField>
  );

  if (sortedRecord == null) {
    return grid;
  }

  return <RecordContextProvider value={sortedRecord}>{grid}</RecordContextProvider>;
};

export default OneOffChecksTable;
